import { useState } from 'react';

interface CategoryExplanation {
  title: string;
  description: string;
  icon: string;
  color: string;
  factors: string[];
  tips: string[];
}

interface InteractiveCategoryCardProps {
  category: CategoryExplanation;
  score: number;
  onHover?: (categoryName: string | null) => void;
}

export default function InteractiveCategoryCard({ 
  category, 
  score,
  onHover 
}: InteractiveCategoryCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const getScoreColor = () => {
    if (score >= 90) return 'text-green-600 bg-green-50';
    if (score >= 80) return 'text-teal-600 bg-teal-50';
    if (score >= 70) return 'text-blue-600 bg-blue-50';
    if (score >= 60) return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
  };

  const getProgressColor = () => {
    if (score >= 90) return 'bg-green-500';
    if (score >= 80) return 'bg-teal-500';
    if (score >= 70) return 'bg-blue-500';
    if (score >= 60) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div 
      className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden transition-all duration-300 hover:border-teal-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer"
      onMouseEnter={() => {
        setShowTooltip(true);
        onHover?.(category.title);
      }}
      onMouseLeave={() => {
        setShowTooltip(false);
        onHover?.(null);
      }}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      {/* Card Header */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div 
            className={`w-14 h-14 flex items-center justify-center rounded-xl transition-all duration-300 ${
              showTooltip ? 'scale-110 rotate-3' : 'scale-100'
            }`}
            style={{ backgroundColor: `${category.color}20` }}
          >
            <i 
              className={`${category.icon} text-3xl`}
              style={{ color: category.color }}
            ></i>
          </div>
          <div className="text-right">
            <div className={`text-3xl font-bold ${getScoreColor().split(' ')[0]}`}>
              {score}
            </div>
            <div className="text-xs text-gray-500">/ 100</div>
          </div>
        </div>

        <h3 className="text-xl font-bold text-gray-900 mb-2">{category.title}</h3>
        <p className="text-sm text-gray-600 mb-4">{category.description}</p>

        {/* Progress Bar */}
        <div className="relative w-full bg-gray-200 rounded-full h-3 mb-3 overflow-hidden">
          <div
            className={`${getProgressColor()} h-3 rounded-full transition-all duration-1000 ease-out relative`}
            style={{ 
              width: `${score}%`,
              animation: 'progressFill 1.5s ease-out forwards'
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white opacity-30"></div>
          </div>
        </div>

        {/* Expand/Collapse Indicator */}
        <button className="flex items-center gap-2 text-sm font-semibold text-teal-600 hover:text-teal-700 transition-colors w-full justify-between whitespace-nowrap">
          <span>{isExpanded ? 'Hide Details' : 'Show Details'}</span>
          <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-lg transition-transform duration-300`}></i>
        </button>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t-2 border-gray-200 bg-gray-50 p-6 animate-slideDown">
          {/* Key Factors */}
          <div className="mb-6">
            <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <i className="ri-file-list-line text-teal-600"></i>
              Key Factors
            </h4>
            <ul className="space-y-2">
              {category.factors.map((factor, idx) => (
                <li 
                  key={idx} 
                  className="flex items-start gap-2 text-sm text-gray-700 animate-slideInRight"
                  style={{ animationDelay: `${idx * 50}ms` }}
                >
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5 flex-shrink-0"></i>
                  <span>{factor}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Improvement Tips */}
          <div>
            <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <i className="ri-lightbulb-line text-amber-500"></i>
              How to Improve
            </h4>
            <ul className="space-y-2">
              {category.tips.map((tip, idx) => (
                <li 
                  key={idx} 
                  className="flex items-start gap-2 text-sm text-gray-700 animate-slideInLeft"
                  style={{ animationDelay: `${idx * 50}ms` }}
                >
                  <i className="ri-arrow-up-circle-fill text-green-600 mt-0.5 flex-shrink-0"></i>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Hover Tooltip */}
      {showTooltip && !isExpanded && (
        <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 -translate-y-full z-10 animate-fadeIn pointer-events-none">
          <div className="bg-gray-900 text-white text-xs rounded-lg px-4 py-2 shadow-xl max-w-xs">
            <div className="font-semibold mb-1">Click to explore details</div>
            <div className="text-gray-300">
              See what affects this score and how to improve it
            </div>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
              <div className="border-6 border-transparent border-t-gray-900"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
